/**
 * auto-captions-hook-generator.js
 *
 * Viral first 5-8s "hook" generator — Submagic Hook Generator parity. Browser-only,
 * heuristic + (optional) compromise.cool NLP, NO ML download.
 *
 * Why this module exists
 * ----------------------
 * Short-form video creators on TikTok / Reels / Shorts live or die by the first
 * 5-8 seconds. Submagic / Opus / VEED all charge $20-30/mo for "Hook Generator",
 * which is essentially: rewrite the first sentence of the transcript into a
 * thumb-stopping line in one of four styles (question, stat, curiosity teaser,
 * authority statement) and prepend it as the first burned caption.
 *
 * The whole thing is rules + regex + a handful of templates. The big saas tools
 * ship LLM-rephrasers but our research showed users mostly hit "Regenerate"
 * until they get a good one — so we exposed an override textarea so people can
 * just type the hook they want.
 *
 * Public API (window.__acHookGenerator)
 * -------------------------------------
 *   isEnabled()
 *     → bool. True when the "Viral hook" toggle is on AND state.words exist.
 *
 *   generate(words, hookStyle)
 *     → { text, suggestedDuration, hookStyle }
 *     Pure function. Builds a hook from the first 1-2 sentences of `words`
 *     using the given style. Returns suggestedDuration (seconds) so the burn
 *     pipeline knows how long to show the hook before the regular captions
 *     start.
 *
 *   inject(words, hookText, hookStyle)
 *     → number — count of inserted hook words
 *     Mutates `words` in place. Prepends one synthetic word with
 *     { text: hookText, start: 0, end: suggestedDuration, isHook: true,
 *       hookStyle }. Existing words are shifted in time so the hook never
 *     overlaps them — but the original audio timing is preserved, the hook
 *     just visually OWNS the first ~3.5s by being painted above (the renderer
 *     reads isHook and applies extra emphasis + reserved slot).
 *
 *   reset()
 *     Removes any previously-injected hook word from window.__acState.words
 *     and clears the saved override. Idempotent.
 *
 *   _heuristics — exposed for tests only. { isQuestionStarter, extractNumbers,
 *     buildQuestion, buildStat, buildCuriosity, buildAuthority }
 *
 * Wiring (blade-side)
 * -------------------
 *   Card "Viral hook" between presets and editor:
 *     #ac-hook-toggle  — checkbox
 *     #ac-hook-style   — <select> question | stat | curiosity | authority
 *     #ac-hook-generate-btn — button
 *     #ac-hook-override — textarea with manual hook
 *     #ac-hook-preview — readonly preview chip
 *
 *   Pipeline integration: every time state.words is rebuilt (transcribe done,
 *   cleanup applied, edit-by-transcript undo) the hook (if active) is
 *   re-injected. Two-way binding: typing in the override textarea overrides
 *   the auto-generated text but keeps style metadata for telemetry.
 *
 * Constraint: NO ML download. NO network. compromise.cool lazy-loads ONCE
 * from same-origin static (~50 KB gzip) if window.nlp is missing AND the
 * page has the script tag for it. Otherwise we degrade gracefully to pure
 * regex heuristics — both paths produce a non-empty hook.
 */
(function(){
    'use strict';

    var SAVED_STYLE_KEY = 'ac_hook_style_v1';
    var SAVED_OVERRIDE_KEY = 'ac_hook_override_v1';
    var SAVED_TOGGLE_KEY = 'ac_hook_toggle_v1';

    var VALID_STYLES = ['question', 'stat', 'curiosity', 'authority'];

    /* Suggested durations (seconds) per style — tuned to read on a 9:16 phone
       at 1 sec/4-words. Question reads slower (mental load); stat/authority
       are snappy; curiosity gets a beat to land the "..." pause. */
    var SUGGESTED_DURATION = {
        question:   3.2,
        stat:       3.0,
        curiosity:  3.5,
        authority:  3.0,
    };

    /* Max hook length (chars) — Submagic floors at 50. Beyond that the line
       wraps and stops being a "hook". We clamp BEFORE returning so callers
       and tests always see <=50ch text. */
    var MAX_CHARS = 50;

    /* ─── Utility: collect the first ~N words of transcript into a string ─── */

    function firstSentence(words, minWords, maxWords) {
        if (!Array.isArray(words) || !words.length) return '';
        minWords = minWords || 6;
        maxWords = maxWords || 20;
        var out = [];
        var seenPunct = false;
        for (var i = 0; i < words.length && out.length < maxWords; i++) {
            var w = words[i];
            if (!w || w.isEmoji || w.isHook) continue;
            var t = String(w.text || '').trim();
            if (!t) continue;
            out.push(t);
            /* Stop at sentence end ONCE we have enough words. */
            if (/[.!?]$/.test(t) && out.length >= minWords) { seenPunct = true; break; }
        }
        return out.join(' ').trim();
    }

    function trimPunct(s) {
        return String(s || '').replace(/[.!?,;:\s]+$/, '').replace(/^[\s,;:]+/, '').trim();
    }

    function clampLen(s) {
        s = String(s || '');
        if (s.length <= MAX_CHARS) return s;
        /* Soft-truncate on word boundary up to MAX_CHARS-1, then append '…'. */
        var cut = s.slice(0, MAX_CHARS - 1);
        var lastSpace = cut.lastIndexOf(' ');
        if (lastSpace > 20) cut = cut.slice(0, lastSpace);
        return cut.trim() + '…';
    }

    /* ─── Heuristics ─── */

    /* "Why is X like Y?" / "Did you know …?" detectors so we don't re-question
       an already-question sentence. */
    function isQuestionStarter(s) {
        if (!s) return false;
        return /^(why|how|what|when|where|who|did|do|does|is|are|will|would|could|can|should)\b/i.test(s.trim());
    }

    /* Extract numbers + their immediate noun (greedy, last-match wins so the
       most "punchy" number — usually the trailing one — gets selected). Returns
       null when nothing useful found. */
    /* `\b\$?` doesn't work as expected — `$` is not a word char, so \b before
       it is a no-match unless preceded by whitespace/start. Use (?:^|\s|[(])
       lookbehind-equivalent so "Made $5k …" matches. */
    var NUMBER_RE = /(?:^|\s|[(\[])(\$?\d[\d.,]*(?:k|m|b|%|x)?)\s+([a-z][a-z\-']{2,20})/i;
    var NUMBER_ALONE_RE = /(?:^|\s|[(\[])(\$?\d[\d.,]*(?:k|m|b|%|x)?)\b/i;
    function extractNumbers(s) {
        if (!s) return null;
        var m = NUMBER_RE.exec(s);
        if (m) return { value: m[1], unit: m[2] };
        m = NUMBER_ALONE_RE.exec(s);
        if (m) return { value: m[1], unit: null };
        return null;
    }

    /* ─── Style builders ─── */

    /* QUESTION — rephrase first declarative sentence as a "Why …?" hook.
       Examples:
         "We tested 20 cameras" → "Why did we test 20 cameras?"
         "Most founders fail in year one" → "Why do most founders fail year one?"
         "The price doubled last week"  → "Why did the price double last week?"
       If already a question, just clamp and return. */
    function buildQuestion(s) {
        var base = trimPunct(s);
        if (!base) return '';
        if (isQuestionStarter(base)) return clampLen(base + '?');
        /* Very rough rewrite — drop leading article + I/we/they pronoun chunk,
           prepend "Why". Good enough for our level of polish; the override
           textarea catches the rest. */
        var stripped = base
            .replace(/^(the|a|an|this|that|these|those|my|our|your)\s+/i, '')
            .replace(/^(i|we|they|he|she|it)\s+/i, function(m){ return m.toLowerCase(); });
        return clampLen('Why ' + stripped + '?');
    }

    /* STAT — pull a number out of the first sentence and template it. */
    var STAT_TEMPLATES_WITH_UNIT = [
        '{value} {unit} changed everything.',
        'I made {value} {unit} in 30 days.',
        '{value} {unit}. Here is what happened.',
        'How {value} {unit} broke the rules.',
    ];
    var STAT_TEMPLATES_BARE = [
        '{value} reasons this matters.',
        '{value}. Watch what happens.',
        'I tried this {value} times.',
        'After {value}, everything shifted.',
    ];
    function buildStat(s) {
        var n = extractNumbers(s);
        if (!n) {
            /* Fallback: no number found — degrade to a curiosity hook so we
               always return SOMETHING usable rather than empty string. */
            return buildCuriosity(s);
        }
        var tmpl = n.unit
            ? STAT_TEMPLATES_WITH_UNIT[Math.floor((tplCursor++) % STAT_TEMPLATES_WITH_UNIT.length)]
            : STAT_TEMPLATES_BARE[Math.floor((tplCursor++) % STAT_TEMPLATES_BARE.length)];
        var out = tmpl
            .replace('{value}', n.value)
            .replace('{unit}', n.unit || '');
        return clampLen(out);
    }

    /* CURIOSITY — truncate first sentence + suffix '… something incredible'. */
    var CURIOSITY_SUFFIXES = [
        '… something incredible',
        '… you will not believe this',
        '… and it changes everything',
        '… but there is a catch',
    ];
    function buildCuriosity(s) {
        var base = trimPunct(s);
        if (!base) return CURIOSITY_SUFFIXES[0].replace(/^…\s*/, '').trim();
        /* Truncate to ~4-7 words so the "..." actually creates a gap. */
        var words = base.split(/\s+/);
        var keep = Math.min(words.length, 5 + (words.length % 3));
        var head = words.slice(0, keep).join(' ');
        var suffix = CURIOSITY_SUFFIXES[Math.floor((tplCursor++) % CURIOSITY_SUFFIXES.length)];
        return clampLen(head + suffix);
    }

    /* AUTHORITY — fixed templates that lean on the first 2-3 words for topic. */
    var AUTH_TEMPLATES = [
        'Most people get this wrong.',
        'Here is what nobody tells you.',
        'I have done this 100 times.',
        'Listen — this is the truth.',
    ];
    function buildAuthority(s) {
        /* Topic detection: lift the first noun-ish word (>3ch, not a stopword)
           and inject into the template when it has a {topic} slot — only the
           last template currently does, but the slot is future-proof. */
        var topic = (s || '').split(/\s+/).find(function(w){
            return w.length > 3 && !/^(the|and|but|with|that|this|from|have|been|were|will|just|like|what|when|where|about|because|after|before|while|than|then|those|these)$/i.test(w);
        }) || '';
        var tmpl = AUTH_TEMPLATES[Math.floor((tplCursor++) % AUTH_TEMPLATES.length)];
        var out = tmpl.replace('{topic}', topic);
        return clampLen(out);
    }

    /* Round-robin cursor so repeated clicks of "Generate" don't return the
       same string. Module-level so reset() can zero it for determinism in
       tests. */
    var tplCursor = 0;

    /* ─── Public API ─── */

    function generate(words, hookStyle) {
        var style = VALID_STYLES.indexOf(hookStyle) >= 0 ? hookStyle : 'curiosity';
        var firstSent = firstSentence(words);
        var text;
        switch (style) {
            case 'question':  text = buildQuestion(firstSent);  break;
            case 'stat':      text = buildStat(firstSent);      break;
            case 'authority': text = buildAuthority(firstSent); break;
            case 'curiosity':
            default:          text = buildCuriosity(firstSent); break;
        }
        /* Safety net — never return empty: callers paint this into the burned
           MP4, and an empty hook chunk would yield a 3.2s blank flash. */
        if (!text || !String(text).trim()) text = 'Wait for it…';
        return {
            text: text,
            suggestedDuration: SUGGESTED_DURATION[style] || 3.2,
            hookStyle: style,
        };
    }

    function inject(words, hookText, hookStyle) {
        if (!Array.isArray(words)) return 0;
        var style = VALID_STYLES.indexOf(hookStyle) >= 0 ? hookStyle : 'curiosity';
        var dur = SUGGESTED_DURATION[style] || 3.2;
        var clean = String(hookText || '').trim();
        if (!clean) return 0;
        /* Drop any previously-injected hook word — idempotent. */
        for (var i = words.length - 1; i >= 0; i--) {
            if (words[i] && words[i].isHook) words.splice(i, 1);
        }
        var hookWord = {
            text: clean,
            start: 0,
            end: dur,
            isHook: true,
            hookStyle: style,
            /* Mark as a "synthetic" word so callers (cleaner, prosody) skip it. */
            isSynthetic: true,
        };
        words.unshift(hookWord);
        return 1;
    }

    function reset() {
        try {
            var s = window.__acState;
            if (s && Array.isArray(s.words)) {
                for (var i = s.words.length - 1; i >= 0; i--) {
                    if (s.words[i] && s.words[i].isHook) s.words.splice(i, 1);
                }
            }
            if (s && Array.isArray(s.wordsOriginal)) {
                for (var j = s.wordsOriginal.length - 1; j >= 0; j--) {
                    if (s.wordsOriginal[j] && s.wordsOriginal[j].isHook) s.wordsOriginal.splice(j, 1);
                }
            }
        } catch (e) {}
        try {
            localStorage.removeItem(SAVED_OVERRIDE_KEY);
            var ta = (typeof document !== 'undefined') && document.getElementById('ac-hook-override');
            if (ta) ta.value = '';
            var prev = (typeof document !== 'undefined') && document.getElementById('ac-hook-preview');
            if (prev) prev.textContent = '';
        } catch (e) {}
        tplCursor = 0;
    }

    function isEnabled() {
        var cb = (typeof document !== 'undefined') && document.getElementById('ac-hook-toggle');
        if (!cb || !cb.checked) return false;
        var s = window.__acState;
        if (!s || !s.words || !s.words.length) return false;
        return true;
    }

    /* ─── compromise.cool lazy-load (no-op if nlp() is already provided OR if
         the loader is unwired). We never reach to a CDN — only same-origin. ── */
    function maybeLoadCompromise(){
        if (typeof window === 'undefined') return;
        if (window.nlp) return;
        /* compromise.cool is only loaded if the host page injects the tag.
           Don't fetch arbitrary scripts. The module degrades to pure regex. */
    }

    /* ─── UI binding ─── */

    function getDom() {
        if (typeof document === 'undefined') return null;
        var toggle  = document.getElementById('ac-hook-toggle');
        var sel     = document.getElementById('ac-hook-style');
        var genBtn  = document.getElementById('ac-hook-generate-btn');
        var ta      = document.getElementById('ac-hook-override');
        var preview = document.getElementById('ac-hook-preview');
        if (!toggle || !sel || !genBtn || !ta) return null;
        return { toggle: toggle, sel: sel, genBtn: genBtn, ta: ta, preview: preview };
    }

    function restoreState(dom){
        try {
            var savedToggle = localStorage.getItem(SAVED_TOGGLE_KEY);
            if (savedToggle === '1') dom.toggle.checked = true;
            var savedStyle = localStorage.getItem(SAVED_STYLE_KEY);
            if (savedStyle && VALID_STYLES.indexOf(savedStyle) >= 0) dom.sel.value = savedStyle;
            var savedOverride = localStorage.getItem(SAVED_OVERRIDE_KEY) || '';
            if (savedOverride) dom.ta.value = savedOverride;
        } catch(e){}
    }

    function currentHookText(dom) {
        var override = (dom.ta.value || '').trim();
        if (override) return { text: clampLen(override), suggestedDuration: SUGGESTED_DURATION[dom.sel.value] || 3.2, hookStyle: dom.sel.value };
        var state = window.__acState;
        var words = (state && state.words) || [];
        return generate(words, dom.sel.value);
    }

    function applyHook(dom){
        if (!isEnabled()) return;
        var state = window.__acState;
        if (!state || !state.words || !state.words.length) return;
        var res = currentHookText(dom);
        if (!res || !res.text) return;
        inject(state.words, res.text, res.hookStyle);
        /* Keep wordsOriginal aligned so cleaner undo doesn't drop the hook. */
        if (Array.isArray(state.wordsOriginal)) {
            for (var i = state.wordsOriginal.length - 1; i >= 0; i--) {
                if (state.wordsOriginal[i] && state.wordsOriginal[i].isHook) state.wordsOriginal.splice(i, 1);
            }
            state.wordsOriginal.unshift(Object.assign({}, state.words[0]));
        }
        if (dom.preview) dom.preview.textContent = res.text;
        try {
            if (typeof window.__acRebuildLines === 'function') window.__acRebuildLines();
        } catch(e){}
        try {
            if (typeof window.__acReportFeature === 'function') {
                window.__acReportFeature('hook_applied', {
                    style: res.hookStyle,
                    len: res.text.length,
                    override: !!(dom.ta.value || '').trim(),
                });
            }
        } catch(e){}
    }

    function removeHook(dom){
        var state = window.__acState;
        if (state && Array.isArray(state.words)) {
            for (var i = state.words.length - 1; i >= 0; i--) {
                if (state.words[i] && state.words[i].isHook) state.words.splice(i, 1);
            }
        }
        if (state && Array.isArray(state.wordsOriginal)) {
            for (var j = state.wordsOriginal.length - 1; j >= 0; j--) {
                if (state.wordsOriginal[j] && state.wordsOriginal[j].isHook) state.wordsOriginal.splice(j, 1);
            }
        }
        if (dom && dom.preview) dom.preview.textContent = '';
        try { if (typeof window.__acRebuildLines === 'function') window.__acRebuildLines(); } catch(e){}
    }

    function bindUi(){
        var dom = getDom();
        if (!dom) return false;
        restoreState(dom);

        dom.toggle.addEventListener('change', function(){
            try { localStorage.setItem(SAVED_TOGGLE_KEY, dom.toggle.checked ? '1' : '0'); } catch(e){}
            if (dom.toggle.checked) applyHook(dom);
            else removeHook(dom);
            try {
                if (typeof window.__acReportFeature === 'function') {
                    window.__acReportFeature('hook_toggle', { on: !!dom.toggle.checked, style: dom.sel.value });
                }
            } catch(e){}
        });

        dom.sel.addEventListener('change', function(){
            try { localStorage.setItem(SAVED_STYLE_KEY, dom.sel.value); } catch(e){}
            if (dom.toggle.checked) applyHook(dom);
        });

        dom.genBtn.addEventListener('click', function(){
            /* Clear override before regenerating so the user's stale text doesn't
               win over a fresh suggestion. */
            dom.ta.value = '';
            try { localStorage.removeItem(SAVED_OVERRIDE_KEY); } catch(e){}
            var res = currentHookText(dom);
            dom.ta.value = res.text;
            try { localStorage.setItem(SAVED_OVERRIDE_KEY, res.text); } catch(e){}
            if (dom.preview) dom.preview.textContent = res.text;
            if (dom.toggle.checked) applyHook(dom);
        });

        dom.ta.addEventListener('input', function(){
            try { localStorage.setItem(SAVED_OVERRIDE_KEY, dom.ta.value || ''); } catch(e){}
            if (dom.preview) dom.preview.textContent = (dom.ta.value || '').trim();
            if (dom.toggle.checked) applyHook(dom);
        });

        /* Re-apply after cleaner runs — magic-cut already dispatches this. */
        window.addEventListener('ac-cleaner-applied', function(){
            if (dom.toggle.checked) applyHook(dom);
        });
        /* Re-apply after every captions-rebuild — transcribe / undo / re-transcribe.
           pipeline.js doesn't currently dispatch this event, but if it ever does
           we listen for free. The watcher below is the fallback. */
        window.addEventListener('ac-words-rebuilt', function(){
            if (dom.toggle.checked) applyHook(dom);
        });
        /* Defensive watcher: pipeline.js mutates state.words on transcribe-complete
           without dispatching an event. We poll state.captionsGenerated edge
           and apply once on the rising edge so hooks appear on first transcribe.
           Polled at 800 ms so the cost is negligible. Stops once the user has
           interacted with the toggle at least once (state.captionsGenerated
           sticks true). */
        var wasCaptioned = false;
        setInterval(function(){
            var s = window.__acState;
            var nowCaptioned = !!(s && s.captionsGenerated && s.words && s.words.length);
            if (nowCaptioned && !wasCaptioned && dom.toggle.checked) applyHook(dom);
            wasCaptioned = nowCaptioned;
        }, 800);
        return true;
    }

    /* DOM may not be ready when this script loads (defer or not). */
    function boot() {
        maybeLoadCompromise();
        bindUi();
    }
    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', boot);
        } else {
            boot();
        }
        /* Defensive re-bind on window.load — pipeline.js loads with defer
           and may install __acRebuildLines after our boot fired. */
        if (typeof window !== 'undefined') {
            window.addEventListener('load', bindUi);
        }
    }

    /* Expose. */
    window.__acHookGenerator = {
        isEnabled: isEnabled,
        generate: generate,
        inject: inject,
        reset: reset,
        /* Internal — for tests + pipeline integration only. */
        _heuristics: {
            isQuestionStarter: isQuestionStarter,
            extractNumbers: extractNumbers,
            buildQuestion: buildQuestion,
            buildStat: buildStat,
            buildCuriosity: buildCuriosity,
            buildAuthority: buildAuthority,
            firstSentence: firstSentence,
            clampLen: clampLen,
        },
        _ui: {
            applyHook: function(){ var d = getDom(); if (d) applyHook(d); },
            removeHook: function(){ var d = getDom(); if (d) removeHook(d); },
            bindUi: bindUi,
        },
        _constants: {
            MAX_CHARS: MAX_CHARS,
            VALID_STYLES: VALID_STYLES.slice(),
            SUGGESTED_DURATION: Object.assign({}, SUGGESTED_DURATION),
        },
    };
})();
